import React, { memo, useCallback, useRef, useState } from "react";
import SearchIcon from "@material-ui/icons/Search";
import ControlButton from "components/common/control-button";
import useStyles from "./search-menu.styles";
import { isiOS } from "utils/user-agent";
import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";
import PropTypes from "prop-types";
import Box from "@material-ui/core/Box";
import InputAdornment from "@material-ui/core/InputAdornment";
import CloseIcon from "@material-ui/icons/Close";
import Input from "@material-ui/core/Input";
import { useTranslation } from "react-i18next";
import Loader from "components/loader";
import List from "@material-ui/core/List";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "hook/use-media-query";
import Button from "@material-ui/core/Button";
import clsx from "clsx";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import { READER_THEMES } from "utils/themes";
import { SearchTypes } from "./search-menu.constants";


/**
 * Component for rendering the search menu in the epub reader.
 * It includes a button to activate, and a slideable drawer for
 * the search input and the results.
 */
const SearchMenu = memo((props) => {
    const {
        activeTheme,
        controlBtnProps,
        showSearchTypeTabs,
        showSearchMenu,
        searchButtonEnabled,
        globalSearchEnabled,
        handleSearch,
        onResultClick,
        afterInputCleared,
        onSearchTypeChange,
        searchResults,
        isLoading,
        message,
        inputValue,
        onInputChange,
        searchType,
    } = props;
    const inputRef = useRef();
    const isIOSDevice = isiOS();
    const { t } = useTranslation();
    const isTablet = useMediaQuery('tablet');
    const classes = useStyles({ activeTheme, isTablet });

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const toggleDrawer = useCallback(
        (overrideState) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setIsMenuOpen((prev) => overrideState ?? !prev);
    }, [setIsMenuOpen, inputRef.current]);

    const clearSearchInput = useCallback(
    (event) => {
        event.preventDefault();
        inputRef.current.focus();
        afterInputCleared && afterInputCleared();
    }, [setIsMenuOpen, inputRef.current, afterInputCleared]);


    return (
        <>{showSearchMenu && (
            <>
            <ControlButton
                {...controlBtnProps}
                onClick={toggleDrawer(null)}
                icon={SearchIcon}
                aria-label={t('reader_icon_search')}
                title={t('reader_icon_search')}
            />
            <SwipeableDrawer
                disableScrollLock
                disableSwipeToOpen
                disableDiscovery={isIOSDevice}
                disableBackdropTransition={!isIOSDevice}
                anchor={isTablet ? 'bottom' : 'left'}
                open={isMenuOpen}
                onOpen={toggleDrawer(true)}
                onClose={toggleDrawer(false)}
                PaperProps={{
                  className: classes.paper,
                }}
            >
                <Box className={classes.container}>
                    <form onSubmit={handleSearch}>
                        <div className={classes.searchInputContainer}>
                            <Input
                                disableUnderline
                                inputRef={inputRef}
                                variant="standard"
                                value={inputValue}
                                onChange={onInputChange}
                                placeholder={t`ebook_search_results_empty_title`}
                                className={clsx(classes.searchInput, !inputValue && classes.searchInputMuted)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchIcon className={classes.searchIcon}/>
                                    </InputAdornment>
                                }
                                endAdornment={inputValue && (
                                    <InputAdornment position="end">
                                        <CloseIcon onMouseDown={clearSearchInput} className={classes.closeIcon}/>
                                    </InputAdornment>
                                )}
                            />
                            <Button
                                className={classes.searchButton}
                                disabled={!searchButtonEnabled || isLoading}
                                variant="outlined"
                                type="submit"
                            >
                                {t`ebook_search_button`}
                            </Button>
                        </div>
                    </form>
                    <Box className={classes.resultListContainer}>
                        <Box>
                            {showSearchTypeTabs && <Tabs
                                className={classes.tabs}
                                indicatorColor="primary"
                                variant="fullWidth"
                                value={searchType}
                                onChange={onSearchTypeChange}
                                TabIndicatorProps={{ className: classes.tabIndicator }}
                            >
                                <Tab
                                    className={clsx(classes.tab, searchType !== SearchTypes.chapter && classes.tabMuted)}
                                    value={SearchTypes.chapter}
                                    label={t("ebook_search_in_chapter")}
                                />
                                <Tab
                                    className={clsx(classes.tab, searchType !== SearchTypes.book && classes.tabMuted)}
                                    value={SearchTypes.book}
                                    label={t("ebook_search_in_book")}
                                    disabled={!globalSearchEnabled}
                                />
                            </Tabs>}
                        </Box>
                        <Box className={classes.resultListContent}>
                            {isLoading && <Loader/>}
                            {!isLoading && message && <div className={classes.messageContainer}>
                                <Typography className={classes.messageTitle}>{message.title}</Typography>
                                <Typography className={classes.message}>{message.message}</Typography>
                            </div>
                            }
                            {!message && searchResults && !!searchResults.length && (
                                <List>
                                    {searchResults.map((result) => (
                                        <div key={result.uuid} className={classes.result} onClick={onResultClick(result)}>
                                            <div className={classes.resultText}>
                                                <span className={classes.contextText}>...{result.textBefore}</span>
                                                <span className={classes.textMatch}>{result.textMatch}</span>
                                                <span className={classes.contextText}>{result.textAfter}...</span>
                                            </div>
                                            <div className={classes.resultInfo}>
                                                <div className={classes.resultChapter}>{result.title}</div>
                                                <div className={classes.resultPage}>{result.page}</div>
                                            </div>
                                        </div>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </Box>
                </Box>
            </SwipeableDrawer>
            </>)}
        </>
    )
});

SearchMenu.propTypes = {
    activeTheme: PropTypes.oneOf(Object.keys(READER_THEMES)),
    controlBtnProps: PropTypes.shape({
        className: PropTypes.string,
        iconClassName: PropTypes.string,
    }),
    showSearchMenu: PropTypes.bool,
    showSearchTypeTabs: PropTypes.bool,
    searchButtonEnabled: PropTypes.bool,
    globalSearchEnabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    inputValue: PropTypes.string.isRequired,
    searchType: PropTypes.oneOf(Object.values(SearchTypes)),
    message: PropTypes.shape({
        title: PropTypes.string,
        message: PropTypes.string,
    }),
    searchResults: PropTypes.arrayOf(PropTypes.shape({
        uuid: PropTypes.string,
        title: PropTypes.string,
        page: PropTypes.string,
        textBefore: PropTypes.string,
        textMatch: PropTypes.string,
        textAfter: PropTypes.string,
    })),
    handleSearch: PropTypes.func.isRequired,        // (event) => void
    onResultClick: PropTypes.func.isRequired,       // (result) => () => void
    onSearchTypeChange: PropTypes.func,             // (index, value) => void
    onInputChange: PropTypes.func.isRequired,       // (event) => void
    afterInputCleared: PropTypes.func,              // () => void
};

export default SearchMenu;
